import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, getBlobApiErrorMessage } from "../../api/client";
import { toast } from "sonner";
import { tenantTodayDate, toCalendarDateString } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
// Money goes through fmtCurrency, which pins TWO fraction digits.
//
// Every figure on this page was a dollar sign glued to the number's own
// default locale formatting. That default sets a MINIMUM of zero fraction
// digits, so an amount whose cents end in a zero drops it: $464,992.90
// rendered as "$464,992.9" on the Total Paid card, live on Smith Casing.
// It also threw outright on an absent field, where fmtCurrency renders "—".
//
// The expression is described rather than quoted on purpose — a comment that
// spells out the retired form is a live hit for anyone grepping the tree to
// count the money sites still carrying it (CLAUDE.md: a retired thing leaves
// a marker, and the marker is indistinguishable from the thing).
import { fmtCurrency } from "../../lib/display-labels";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import PageHeaderBand, { type PageHeaderStat } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
} from "../../lib/load-failure";
import {
  Coins,
  DollarSign,
  Download,
  FileClock,
  Receipt,
  TrendingUp,
  Wallet,
} from "lucide-react";

/**
 * The preset's range, anchored on the TENANT's today (companies.time_zone) —
 * never the device's. Null with no known zone: the query waits rather than
 * asking the API about a guessed period.
 */
function getDateRange(preset: string, timeZone: string | null): { dateFrom: string; dateTo: string } | null {
  const now = tenantTodayDate(timeZone);
  if (!now) return null;
  const y = now.getFullYear();
  const m = now.getMonth();
  if (preset === "week") {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
    return { dateFrom: toCalendarDateString(d), dateTo: toCalendarDateString(now) };
  }
  if (preset === "month") {
    return { dateFrom: `${y}-${String(m + 1).padStart(2, "0")}-01`, dateTo: toCalendarDateString(now) };
  }
  return { dateFrom: `${y}-01-01`, dateTo: toCalendarDateString(now) };
}

const PERIODS = [
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "ytd", label: "YTD" },
] as const;

/**
 * THE PERIOD PILLS CARRY NO COUNTS, AND THAT IS THE CHIPS-VS-TABS TEST, NOT A
 * SHORTAGE OF FIGURES (CLAUDE.md, settled by fe#704/fe#712).
 *
 * A chip narrows ONE list and every chip asks the same question of the same
 * rows, so a count is meaningful on it. These re-scope the QUERY — each period
 * is a different set of tickets — so a number beside one would be a count of a
 * dataset the other two pills do not contain. They are also not tabs: the page
 * answers the same question in all three states. So: pills, in the body, with
 * no counts.
 */
function PeriodPills({ preset, onPick }: { preset: string; onPick: (k: string) => void }) {
  return (
    <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Reporting period">
      {PERIODS.map((p) => {
        const active = preset === p.key;
        return (
          <button
            key={p.key}
            type="button"
            data-testid="period-pill"
            data-period={p.key}
            aria-pressed={active}
            onClick={() => onPick(p.key)}
            className={`inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
              active
                ? "bg-fi-amber text-fi-amber-ink"
                : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
            }`}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

/** A body panel in the register idiom — eyebrow, heading, then content. */
function Panel({
  eyebrow,
  title,
  icon,
  rail,
  children,
  footer,
}: {
  eyebrow: string;
  title: string;
  icon?: React.ReactNode;
  /** The one amber 4px left edge belongs to ONE panel on the page. */
  rail?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <section
      className={`overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card ${
        rail ? "border-l-4 border-l-fi-amber" : ""
      }`}
    >
      <header className="border-b border-fieldiq-border px-4 py-4">
        <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{eyebrow}</p>
        <h2 className="mt-1 flex items-center gap-2 text-base font-extrabold tracking-tight text-fieldiq-text">
          {icon}
          {title}
        </h2>
      </header>
      <div className="px-4 py-4">{children}</div>
      {footer && (
        <div className="border-t border-fieldiq-border px-4 py-3 text-[11px] text-fieldiq-muted">{footer}</div>
      )}
    </section>
  );
}

/**
 * A body figure. `tone` is the ICON's colour only and every figure is the
 * theme's ink.
 *
 * THE INK USED TO BE `text-emerald-400` AND `text-amber-400` ON TOTAL PAID AND
 * OUTSTANDING, which is a bright dark-mode hue on a WHITE card the moment the
 * app switches (reported in fe#725, fixed here). Neither Tailwind palette class
 * is remapped by theme.css, so nothing reported it: the class is in the DOM,
 * the build is clean, and the amount is simply not readable. CLAUDE.md's answer
 * for a green or red figure is the theme's own ink with the hue carried by the
 * glyph beside it, which is what this does — `text-fieldiq-danger` and
 * `text-fieldiq-success` are NOT the fix (3.44 and 2.53 on a matching wash).
 */
function Figure({
  label,
  value,
  icon,
  iconClass,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  iconClass?: string;
  /** One static line saying what the figure counts. Never a tooltip. */
  sub?: string;
}) {
  return (
    <div>
      <p className="flex items-center gap-1.5 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
        {icon && <span className={`shrink-0 ${iconClass ?? "text-fieldiq-muted"}`}>{icon}</span>}
        <span className="truncate">{label}</span>
      </p>
      <p className="mt-1 font-display text-lg font-extrabold tracking-tight text-fieldiq-text">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-fieldiq-muted">{sub}</p>}
    </div>
  );
}

const CasingFinancialReport = () => {
  const navigate = useNavigate();
  const [preset, setPreset] = useState("ytd");
  const timeZone = useTenantTimeZone();
  const range = useMemo(() => getDateRange(preset, timeZone), [preset, timeZone]);
  const dateFrom = range?.dateFrom;
  const dateTo = range?.dateTo;

  /**
   * `LOAD_FAILURE_QUERY_OPTIONS`, and the query object is kept whole rather
   * than destructured to `{ data, isLoading }`.
   *
   * A two-branch destructure is what made the old page answer every failure
   * with "No financial data for this period." — a sentence about the tenant's
   * books, over a 403 or a dead connection. React Query's default
   * `networkMode: 'online'` also parks a retrying failure in `paused`, where
   * `error` is null, `data` is undefined and `isLoading` is FALSE, so even a
   * branch on `error` would have missed every 5xx while looking correct
   * against a refusal. `queryLoadFailure` reads the failure wherever the query
   * is keeping it.
   */
  const financialQuery = useQuery({
    queryKey: ["reports", "financial", dateFrom, dateTo],
    queryFn: () => api.reports.financialSummary({ dateFrom, dateTo }).then((r) => r.data.data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
    enabled: range !== null,
  });
  const { data, isLoading } = financialQuery;
  const failure = queryLoadFailure(financialQuery);

  const handleExport = async () => {
    try {
      const res = await api.reports.exportCsv("financial", { dateFrom, dateTo });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "fieldiq-financial-report.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // The CSV button used to swallow every failure, so a refused export and
      // a browser that simply did not start the download were the same
      // nothing. `getBlobApiErrorMessage` is the blob-aware reader: on a
      // responseType 'blob' request the API's error JSON arrives as a Blob and
      // `response.data.error` is undefined, so the plain reader would report
      // the fallback for every server message.
      toast.error(await getBlobApiErrorMessage(err, "Failed to export the financial report"));
    }
  };

  const summary = data?.summary;
  const aging = data?.aging;
  const trend = data?.monthly_trend || [];
  const maxTrend = Math.max(...trend.map((t: any) => t.revenue), 1);

  /**
   * FOUR OF THE SIX CARDS, AND THE ORDER IS THE CIRCLE'S, NOT THE NARRATIVE'S.
   *
   * The page had six equal cards; four fit the band and the other two — the
   * CAD/USD split — are a pair that only means anything side by side, so they
   * stayed in the body as their own panel.
   *
   * WHY "DRAFT" IS THIRD AND "OUTSTANDING" IS FOURTH. The third stat's label
   * has a hard ~70px ceiling (CLAUDE.md, fe#698): between 1024 and 1048 wide
   * the hero circle's 28%-amber edge crosses the third column, and a 96px
   * label read 3.88:1 there. "Outstanding" measures 87.6px in this slot's type
   * — over the ceiling — and "Draft" measures 39.5px, so the two swapped
   * places. Measured, not reasoned: see the sweep in the PR body.
   *
   * THE SUB-LINES FOLLOW api#785's PARTITION, and they were wrong before it.
   * Over revenue tickets only (office_approved, customer_approved, invoiced):
   *
   *   Total Paid   = invoiced AND on an invoice whose status is 'paid'
   *   Outstanding  = everything else in revenue: billed-unpaid + unbilled
   *   Revenue      = Paid + Outstanding, by construction in the API
   *
   * The old sub-lines described the old predicates — Paid as "marked
   * invoiced", Outstanding as "approved, unbilled" — and both halves stopped
   * being true together. Paid is now CASH RECEIVED against an invoice, so a
   * tenant whose invoices are all still draft (TNT, Dash, Dutch) reads $0.00
   * here, correctly. The sub-line says so under the figure, because that $0.00
   * is exactly the number someone will report as a bug.
   *
   * BOTH NEW SUB-LINES ARE NARROWER THAN THE ONES THEY REPLACE, and that is
   * what keeps them inside the circle clearance measured for the old copy: a
   * left-aligned line that ends further left cannot reach further into the
   * circle. Widths measured in the PR, not reasoned from character counts.
   * Draft is reported BESIDE the partition, never inside it (#783).
   */
  const bandStats = useMemo((): PageHeaderStat[] => [
    {
      icon: <DollarSign className="size-4" />,
      value: fmtCurrency(summary?.total_revenue),
      label: "Total Revenue",
      sub: "Subtotal, pre-tax",
      tone: "accent",
      loading: isLoading,
    },
    {
      icon: <Wallet className="size-4" />,
      value: fmtCurrency(summary?.total_paid),
      label: "Total Paid",
      sub: "Cash received",
      tone: "success",
      loading: isLoading,
    },
    {
      icon: <FileClock className="size-4" />,
      value: fmtCurrency(summary?.total_draft),
      label: "Draft",
      sub: "Not yet submitted",
      loading: isLoading,
    },
    {
      icon: <Receipt className="size-4" />,
      value: fmtCurrency(summary?.total_outstanding),
      label: "Outstanding",
      sub: "Not yet paid",
      tone: "accent",
      loading: isLoading,
    },
  ], [summary, isLoading]);

  return (
    <div data-testid="financial-report-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global <Header />. Pages never do. */}
      <PageHeaderBand
        eyebrow="Reports"
        title="Financial Summary"
        // /reports, not navigate(-1). fe#725 repointed all eleven standalone
        // report pages at the catalog because history-back is not a
        // destination: entering from Safety sent Back to Safety and a fresh
        // load did nothing at all.
        onBack={() => navigate("/reports")}
        backLabel="Back to reports"
        actions={
          <button
            type="button"
            onClick={handleExport}
            className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
          >
            <Download className="mr-1.5 inline size-4" aria-hidden="true" /> CSV
          </button>
        }
        stats={bandStats}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PeriodPills preset={preset} onPick={setPreset} />

          {isLoading ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-12 text-center">
              <span
                role="status"
                aria-label="Loading the financial summary"
                className="mx-auto block h-7 w-40 animate-pulse rounded-fi-control bg-fieldiq-border"
              />
            </div>
          ) : failure ? (
            /* AN ERROR IS NOT AN EMPTY STATE. This branch stands where "No
               financial data for this period." used to, and it is the whole
               reason the query keeps its options: a 403 on FINANCIAL_READ is an
               ordinary outcome for a supervisor, and a dead zone is an ordinary
               outcome in the field. Neither is a fact about the books. */
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice
                error={queryFailureError(financialQuery)}
                kind={failure}
                subject="the financial summary"
                allowed="admins and office staff"
              />
            </div>
          ) : !summary ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-12 text-center">
              <Coins className="mx-auto mb-3 size-10 text-fieldiq-muted" aria-hidden="true" />
              <p className="text-sm text-fieldiq-text">No financial data for this period.</p>
            </div>
          ) : (
            <>
              {/* THE USD REVENUE FIGURE IS GONE, AND THE PANEL IS NO LONGER
                  A SPLIT.

                  fe#726 restyled this pair and reported the USD half as
                  structurally zero rather than removing it, because that PR
                  restyled and did not change behaviour. This one does change
                  it: `tickets.currency` defaults to 'CAD' and NO write path in
                  this app ever sends a `currency` key — POST /tickets binds
                  `body.currency || 'CAD'` and `currency` appears nowhere in
                  api/client.ts. The three screens that compute one derive it
                  from `company.country`, for display and GST only. So every
                  ticket row in the database is CAD, for every tenant, and the
                  API's `revenue_usd` is `SUM(CASE WHEN t.currency = 'USD' …)`
                  over a set that is empty by construction.

                  A figure whose range is one value is not a figure. It is the
                  same test row 9 of CLAUDE.md's instrument table applies to a
                  measurement — ask what it would read if the thing you fear
                  were true, and if the answer is "the same", nothing is being
                  measured. $0.00 in the largest type the body uses, beside a
                  real CAD total, reads as "we billed nothing in USD this
                  period", which is a claim about the company's books. It is
                  actually a fact about a column nothing populates.

                  WHAT BRINGS IT BACK: a write path that sets
                  `tickets.currency`. POST /tickets would have to accept a
                  `currency` key, `api/client.ts` would have to send one, and
                  the ticket editors would have to let someone choose it —
                  today they derive the symbol from `company.country` and never
                  persist it. Restore this Figure the same day that lands, and
                  put the split title back with it.

                  `revenue_usd` is left ON the payload and untouched in the
                  API: this is a screen declining to draw a figure, not a
                  contract change. The CSV button is unaffected — that export
                  is ticket-level and carries a per-ticket Currency column,
                  never this aggregate. */}
              <Panel
                eyebrow="Revenue"
                title="Ticket revenue"
                icon={<Coins className="size-4 text-fieldiq-accent" aria-hidden="true" />}
                footer={
                  /* A FOOTER EARNS ITS PLACE BY CARRYING A COUNT, A SCOPE, A
                     PERMISSION OR A TIMESTAMP (CLAUDE.md, fe#711). This one is
                     a SCOPE, and it is the reason the figure can be labelled
                     CAD at all: it names the population the sum is taken over.
                     It said "Both figures are…" while there were two. */
                  <>The sum of ticket subtotals in this period. Every ticket carries one currency, and every ticket in FieldIQ is CAD.</>
                }
              >
                <Figure
                  label="CAD Revenue"
                  value={fmtCurrency(summary.revenue_cad, "CAD")}
                  icon={<DollarSign className="size-3.5" aria-hidden="true" />}
                  iconClass="text-fieldiq-accent"
                />
              </Panel>

              <div className="grid gap-4 lg:grid-cols-2">
                <Panel eyebrow="Tax" title="Taxes collected">
                  <div className="grid grid-cols-3 gap-4">
                    <Figure label="GST" value={fmtCurrency(summary.gst_collected)} />
                    <Figure label="PST" value={fmtCurrency(summary.pst_collected)} />
                    <Figure label="HST" value={fmtCurrency(summary.hst_collected)} />
                  </div>
                </Panel>

                {/* COUNTS OF REVENUE TICKETS, NOT OF EVERY TICKET. Since api#785
                    `total_invoices` counts the same population as the revenue
                    figure and the two counts beside it partition it, so
                    Total = Paid + Unpaid. A tenant with no revenue ticket in
                    the period reads a real 0 here (PAR, which read 7 when drafts
                    were counted) — `displayValue` keeps a 0 as 0 and only an
                    absent key becomes "—". "Paid" is the paid-invoice count,
                    no longer "invoiced". */}
                <Panel eyebrow="Volume" title="Revenue tickets in this period">
                  <div className="grid grid-cols-3 gap-4">
                    <Figure label="Total" value={displayValue(summary.total_invoices)} />
                    {/* Was text-emerald-400 / text-amber-400 — see Figure. */}
                    <Figure label="Paid" value={displayValue(summary.paid_invoices)} />
                    <Figure label="Unpaid" value={displayValue(summary.unpaid_invoices)} />
                  </div>
                </Panel>
              </div>

              {/* WHERE THE REVENUE STANDS — api#785's three disjoint buckets,
                  read straight off the payload. Paid + Billed, unpaid +
                  Approved, not billed = Total Revenue on the band, and the last
                  two = Outstanding; the API guarantees both sums, so this page
                  adds nothing up (CLAUDE.md non-negotiable 5).
                  `total_billed_unpaid` and `total_unbilled` were returned and
                  drawn nowhere until this panel. */}
              <Panel
                eyebrow="Receivables"
                title="Where the revenue stands"
                icon={<Wallet className="size-4 text-fieldiq-accent" aria-hidden="true" />}
              >
                <div className="grid gap-4 sm:grid-cols-3">
                  <Figure
                    label="Paid"
                    value={fmtCurrency(summary.total_paid)}
                    sub="Cash received against an invoice, not invoices sent"
                  />
                  <Figure
                    label="Billed, unpaid"
                    value={fmtCurrency(summary.total_billed_unpaid)}
                    sub="Invoiced, payment not yet received"
                  />
                  <Figure
                    label="Approved, not billed"
                    value={fmtCurrency(summary.total_unbilled)}
                    sub="Approved tickets with no invoice yet"
                  />
                </div>
              </Panel>

              {/* AR Aging carries the page's one amber rail: it is the register
                  here — four buckets, read straight off the endpoint. */}
              {aging && (
                <Panel
                  eyebrow="Receivables"
                  title="AR Aging"
                  rail
                  icon={<Receipt className="size-4 text-fieldiq-accent" aria-hidden="true" />}
                  footer={<>Every unpaid revenue ticket in this period, aged from the day the office approved it, in the company's own timezone.</>}
                >
                  <div className="space-y-2">
                    {/* THE FOUR BAR FILLS ARE UNCHANGED, and that is deliberate.
                        They are theme-independent palette fills on a neutral
                        track — four distinguishable severities, one of which
                        (orange) the `--fi-signal-*` family has no member for, so
                        re-spelling them would collapse 31-60 and 61-90 into one
                        colour to gain nothing. A fill is not an ink; the figure
                        beside each bar is the theme's own. */}
                    {[
                      { label: "0-30 days", value: aging["0_30"], bar: "bg-emerald-500" },
                      { label: "31-60 days", value: aging["31_60"], bar: "bg-amber-500" },
                      { label: "61-90 days", value: aging["61_90"], bar: "bg-orange-500" },
                      { label: "90+ days", value: aging["90_plus"], bar: "bg-red-500" },
                    ].map((bucket) => {
                      const total = aging["0_30"] + aging["31_60"] + aging["61_90"] + aging["90_plus"];
                      const pct = total > 0 ? (bucket.value / total) * 100 : 0;
                      return (
                        <div key={bucket.label} className="flex items-center gap-3">
                          <span className="w-16 shrink-0 text-[11px] text-fieldiq-muted">{bucket.label}</span>
                          <div className="h-4 flex-1 overflow-hidden rounded-fi-control bg-fieldiq-dark">
                            <div className={`h-full rounded-fi-control ${bucket.bar}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="w-24 shrink-0 text-right text-[11px] font-semibold text-fieldiq-text">
                            {fmtCurrency(bucket.value)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </Panel>
              )}

              {trend.length > 0 && (
                <Panel
                  eyebrow="Trend"
                  title="Monthly revenue"
                  icon={<TrendingUp className="size-4 text-fieldiq-accent" aria-hidden="true" />}
                  footer={<>Showing {trend.length} month{trend.length === 1 ? "" : "s"} in this period.</>}
                >
                  <div className="space-y-1.5">
                    {trend.map((t: any) => {
                      const pct = (t.revenue / maxTrend) * 100;
                      return (
                        <div key={t.month} className="flex items-center gap-3">
                          <span className="w-24 shrink-0 text-right text-[11px] text-fieldiq-muted">{t.month}</span>
                          <div className="h-5 flex-1 overflow-hidden rounded-fi-control bg-fieldiq-dark">
                            {/* THE FILL BELOW IS UNCHANGED — the accent at 80%. This
                              page is one of the five `accent-signal.test.ts` exempts
                              for exactly this bar, and the exemption is keyed on the
                              path, so re-spelling it would leave a stale entry
                              guarding nothing. */}
                          <div className="h-full rounded-fi-control bg-fieldiq-accent/80" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="w-24 shrink-0 text-right text-[11px] font-semibold text-fieldiq-text">
                            {fmtCurrency(t.revenue)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </Panel>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default CasingFinancialReport;
